import React, { ReactNode, useState } from "react";
import { Box, Collapse, Skeleton } from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import {
  ListContainer,
  ListGroupRow,
  ListLabel,
  ListRow,
  ListScrollArea,
  ListSurface,
  ListSkeletonArea,
  ListTitle,
} from "./SelectList.styled";
import SearchField from "./SearchField";
import EmptyStateCard from "../cards/EmptyStateCard";
import { ediphiPalette } from "../../styles/colors";

export interface SelectListItem<T> {
  id: string;
  label: string;
  group?: string;
  endAdornment?: ReactNode;
  data: T;
}

export interface SelectListProps<T> {
  items: SelectListItem<T>[];
  onSelectItem: (data: T) => void;
  title?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  selectedItemId?: string | null;
  loading?: boolean;
  emptyMessage?: string;
}

// Drawn as an overlay rather than a border or inset shadow: the group headers carry their own
// background, which paints over anything the surface draws beneath them.
const OUTLINED_SURFACE_SX = {
  position: "relative",
  borderRadius: "4px",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
    // border: "1px solid #d0d5dd",
    border: `1px solid ${ediphiPalette.mainBackground}`,
    pointerEvents: "none",
  },
};

export function SelectList<T>({
  items,
  onSelectItem,
  title,
  searchable = true,
  searchPlaceholder = "Search",
  selectedItemId,
  loading = false,
  emptyMessage = "Nothing to show.",
}: SelectListProps<T>) {
  const [searchText, setSearchText] = useState<string>("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const query = searchText.trim().toLowerCase();

  const filtered = query
    ? items.filter((item) => [item.label, item.group ?? ""].some((value) => value.toLowerCase().includes(query)))
    : items;

  const groups = items.some((item) => item.group !== undefined)
    ? Array.from(
        filtered.reduce((map, item) => {
          const key = item.group ?? "";
          return map.set(key, [...(map.get(key) ?? []), item]);
        }, new Map<string, SelectListItem<T>[]>())
      )
    : null;

  const toggleGroup = (key: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderItem = (item: SelectListItem<T>) => (
    <ListRow key={item.id} selected={item.id === selectedItemId} onClick={() => onSelectItem(item.data)} disableRipple>
      <ListLabel>{item.label}</ListLabel>
      {item.endAdornment}
    </ListRow>
  );

  // The empty and no-results states render a card that carries its own border, so the surface is
  // only outlined when it actually holds rows.
  const hasRows = !loading && filtered.length > 0;

  // Hidden only when there is nothing to search. A query that matched nothing keeps the field — it is
  // the only way back. Kept during loading too, since the common case resolves to rows and removing it
  // afterwards would shift the layout more often than adding it.
  const showSearch = searchable && (loading || items.length > 0);

  const renderBody = () => {
    if (loading) {
      return (
        <ListSkeletonArea>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} variant="text" height={40} />
          ))}
        </ListSkeletonArea>
      );
    }

    if (items.length === 0) return <EmptyStateCard text={emptyMessage} />;
    if (filtered.length === 0) return <EmptyStateCard text={"No results"} />;
    // if (filtered.length === 0) return <ListMessage>No results</ListMessage>;
    if (!groups) return filtered.map(renderItem);

    return groups.map(([key, groupItems]) => {
      const isOpen = !collapsed.has(key);
      return (
        <Box key={key}>
          <ListGroupRow onClick={() => toggleGroup(key)} disableRipple>
            <ArrowRightIcon sx={{ fontSize: "18px", transform: isOpen ? "rotate(90deg)" : "none", flexShrink: 0 }} />
            <ListLabel>{key}</ListLabel>
          </ListGroupRow>
          <Collapse in={isOpen}>{groupItems.map(renderItem)}</Collapse>
        </Box>
      );
    });
  };

  return (
    <ListContainer>
      {title && <ListTitle>{title}</ListTitle>}
      {showSearch && <SearchField value={searchText} onChange={setSearchText} placeholder={searchPlaceholder} />}
      <ListScrollArea>
        <ListSurface sx={hasRows ? OUTLINED_SURFACE_SX : undefined}>{renderBody()}</ListSurface>
      </ListScrollArea>
    </ListContainer>
  );
}

export default SelectList;
